import { useCallback, useState } from 'react';
/**
 * A custom React hook that manages an open/closed state.
 *
 * This hook provides a boolean state for tracking whether something is open or closed,
 * along with functions to open and close it. It's useful for managing the state of
 * modals, dropdowns, expandable sections, etc.
 *
 * @param {boolean} initialState - The initial open/closed state.
 * @returns {[boolean, () => void, () => void]} A tuple containing:
 *   - isOpen: A boolean indicating whether the item is open.
 *   - open: A function to set the state to open.
 *   - close: A function to set the state to closed.
 *
 * @example
 * // Basic usage in a modal component
 * function Modal() {
 *   const [isOpen, openModal, closeModal] = useIsOpen(false);
 *
 *   return (
 *     <>
 *       <button onClick={openModal}>Open Modal</button>
 *       {isOpen && (
 *         <div className="modal">
 *           <h2>Modal Content</h2>
 *           <p>This is the modal content.</p>
 *           <button onClick={closeModal}>Close Modal</button>
 *         </div>
 *       )}
 *     </>
 *   );
 * }
 *
 * @example
 * // Usage in a collapsible section component
 * function CollapsibleSection({ title, children }) {
 *   const [isExpanded, expand, collapse] = useIsOpen(false);
 *
 *   return (
 *     <div className="collapsible-section">
 *       <button onClick={isExpanded ? collapse : expand}>
 *         {title} {isExpanded ? '▲' : '▼'}
 *       </button>
 *       {isExpanded && <div className="content">{children}</div>}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Usage with custom naming in a dropdown component
 * function Dropdown({ options }) {
 *   const [isDropdownOpen, showDropdown, hideDropdown] = useIsOpen(false);
 *
 *   return (
 *     <div>
 *       <button onMouseEnter={showDropdown} onMouseLeave={hideDropdown}>
 *         Select an option
 *       </button>
 *       {isDropdownOpen && (
 *         <ul className="dropdown-menu">
 *           {options.map(option => (
 *             <li key={option.value}>{option.label}</li>
 *           ))}
 *         </ul>
 *       )}
 *     </div>
 *   );
 * }
 */
export function useIsOpen(
    initialState: boolean,
): [isOpen: boolean, open: () => void, close: () => void] {
    const [isOpen, setIsOpen] = useState(initialState);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    return [isOpen, open, close];
}
